import React, { useMemo, useState } from 'react';
import { useAppSelector } from "../../redux/hooks";
import PageWithHeader from "../common/PageWithHeader";
import { 
  Grid, 
  Typography, 
  Tabs, 
  Tab, 
  Box, 
  Container, 
  Paper, 
  useTheme,
  useMediaQuery
} from "@mui/material";
import LoggedOutView from "../common/LoggedOutView";
import { makeStyles } from '@mui/styles';
import {theme} from '../../theme/theme';
import {Property, useGetPropertiesOwnerByOwnerIdQuery, useGetPropertiesQuery} from "../../redux/residentPortalApi";
import PropertyCard from "../common/PropertyCard";
import LoadingCover from "../common/LoadingCover";
import { useIsAdmin } from "../../hooks/isAdmin";
import { useHasAnyRole } from "../../hooks/useHasAnyRole";
import HomeIcon from '@mui/icons-material/Home';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import { Helmet } from 'react-helmet-async';

const useStyles = makeStyles(({
  root: {
    padding: theme.spacing(4),
    backgroundColor: '#f8f9fa',
    minHeight: 'calc(100vh - 90px)',
  },
  container: {
    marginTop: theme.spacing(4),
    marginBottom: theme.spacing(8),
  },
  headerSection: {
    marginBottom: theme.spacing(6),
  },
  header: {
    fontWeight: 700,
    color: '#2c3e50',
    marginBottom: theme.spacing(1),
  },
  subheader: {
    color: '#7f8c8d',
    marginBottom: theme.spacing(4),
  },
  tabsContainer: {
    backgroundColor: '#fff',
    borderRadius: '12px',
    boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
    marginBottom: theme.spacing(6),
  },
  tabs: {
    '& .MuiTab-root': {
      textTransform: 'none',
      fontWeight: 600,
      fontSize: '1rem',
      minHeight: '60px',
    },
    '& .Mui-selected': {
      color: '#3498db',
    },
    '& .MuiTabs-indicator': {
      backgroundColor: '#3498db',
      height: '3px',
    },
  },
  tabIcon: {
    marginRight: theme.spacing(1),
  },
  propertiesGrid: {
    marginTop: theme.spacing(4),
  },
  noProperties: {
    textAlign: 'center',
    padding: theme.spacing(8),
    color: '#7f8c8d',
    backgroundColor: '#fff',
    borderRadius: '12px',
    boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
  }
}));

const Dashboard: React.FC = () => {
  const classes = useStyles();
  const muiTheme = useTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('sm'));
  const user = useAppSelector(state => state.auth.user);
  const isAdmin = useIsAdmin();
  const canViewAllProperties = useHasAnyRole(['admin', 'residents-support']);
  const [viewMode, setViewMode] = useState<'my' | 'admin'>(canViewAllProperties ? 'admin' : 'my');
  
  const {data: myProperties, isLoading: myPropertiesIsLoading} = useGetPropertiesOwnerByOwnerIdQuery(
    {ownerId: user?.id ?? ''}, 
    { skip: !user || (canViewAllProperties && viewMode === 'admin') }
  );
  
  const {data: adminProperties, isLoading: adminPropertiesIsLoading} = useGetPropertiesQuery(
    undefined, 
    { skip: !canViewAllProperties || viewMode === 'my' }
  );

  const sortedAdminProperties = useMemo(() => {
    if (!adminProperties) return adminProperties;

    return [...adminProperties].sort((a, b) => {
      const aKey = `${a.development ?? ''}-${a.phase ?? ''}-${String(a.unitNumber ?? '')}`;
      const bKey = `${b.development ?? ''}-${b.phase ?? ''}-${String(b.unitNumber ?? '')}`;

      return aKey.localeCompare(bKey, undefined, { numeric: true, sensitivity: 'base' });
    });
  }, [adminProperties]);

  if (!user) {
    return <LoggedOutView />
  }

  const isLoading = (viewMode === 'my' && myPropertiesIsLoading) || 
                    (viewMode === 'admin' && adminPropertiesIsLoading);
  
  if (isLoading) {
    return <LoadingCover/>
  }

  const properties = viewMode === 'my' ? myProperties : sortedAdminProperties;
  
  const handleTabChange = (event: React.SyntheticEvent, newValue: 'my' | 'admin') => {
    setViewMode(newValue);
  };

  return (
    <PageWithHeader>
      <Helmet>
        <title>{viewMode === 'my' ? 'My Properties | WestProp Residents Portal' : `${isAdmin ? 'Admin' : 'All'} Properties | WestProp Residents Portal`}</title>
        <meta name="description" content={viewMode === 'my' ? 'View and manage your properties' : `${isAdmin ? 'Manage' : 'View'} all properties in the system`} />
      </Helmet>
      <Box className={classes.root}>
        <Container maxWidth="xl" className={classes.container}>
          <Box className={classes.headerSection}>
            <Typography variant='h1' className={classes.header}>
              {viewMode === 'my' ? 'My Properties' : isAdmin ? 'Admin Properties' : 'All Properties'}
            </Typography>
            <Typography variant='body1' className={classes.subheader}>
              {viewMode === 'my' 
                ? 'View and manage your properties' 
                : isAdmin ? 'Manage all properties in the system' : 'View all properties in the system'}
            </Typography>
            
            {isAdmin && (
              <Paper className={classes.tabsContainer} elevation={0}>
                <Tabs 
                  value={viewMode} 
                  onChange={handleTabChange} 
                  aria-label="property view tabs"
                  className={classes.tabs}
                  variant={isMobile ? "fullWidth" : "standard"}
                >
                  <Tab 
                    icon={<HomeIcon className={classes.tabIcon} />} 
                    iconPosition="start"
                    label="My Properties" 
                    value="my" 
                  />
                  <Tab 
                    icon={<AdminPanelSettingsIcon className={classes.tabIcon} />} 
                    iconPosition="start"
                    label="Admin Properties" 
                    value="admin" 
                  />
                </Tabs>
              </Paper>
            )}
          </Box>
          
          <Grid container spacing={3} className={classes.propertiesGrid}>
            {properties && properties.length > 0 ? (
              properties.map((property: Property) => (
                <PropertyCard property={property} key={property.id} />
              ))
            ) : (
              <Grid item xs={12}>
                <Paper className={classes.noProperties} elevation={0}>
                  <Typography variant="h5">
                    No properties to show
                  </Typography>
                  <Typography variant="body1">
                    {viewMode === 'my' 
                      ? 'You don\'t have any properties assigned to you yet.' 
                      : 'There are no properties in the system yet.'}
                  </Typography>
                </Paper>
              </Grid>
            )}
          </Grid>
        </Container>
      </Box>
    </PageWithHeader>
  );
};

export default Dashboard;
